import type {
  AiAnswerStatus,
  ChatMessage,
  ChatSession,
  ChatWidgetAIConfig,
  ChatWidgetStorageConfig,
  ChatWidgetZendeskConfig,
  FeedbackRating,
  Source,
  StreamCallbacks
} from '@hevoio/hevo-ui-toolkit';
import { ConversationStatus, CustomerPriority, MessageRole } from '@hevoio/hevo-ui-toolkit';
import { AppConfig } from '../../../core/app.config';
import { SESSION_KEY } from '../../../core/constants/constants';
import {
  ChatbotEventTracker,
  ChatbotTracking,
  SLOW_RESPONSE_THRESHOLD_MS
} from './chatbot-tracking';

const appConfig = new AppConfig();
// will be removed later
const NGROK_FALLBACK = 'https://sketch-satiable-willpower.ngrok-free.dev'; // local ngrok fallback

// fallback for braavos
const useFallback = window.location.hostname.startsWith('s-');
const CEREBRA_BASE = useFallback ? NGROK_FALLBACK : appConfig.getCerebraURL();
const STORAGE_KEY = 'cerebra_session_hevo_ui';

export function makeCerebraStorage(): ChatWidgetStorageConfig {
  return {
    getSessionId: () => localStorage.getItem(STORAGE_KEY),
    setSessionId: (id: string) => localStorage.setItem(STORAGE_KEY, id),
    clearSession: () => localStorage.removeItem(STORAGE_KEY)
  };
}

// Tracks which session the user has open. Set the moment fetchSessionMessages
// is called. pollMessages suppresses message delivery for any other session
// so background polling never fills the pending buffer with messages that
// fetchSessionMessages will also return — preventing duplicates on load.
let activeSessionId: string | null = null;

// Per-conversation guard for pollMessages. Aborts the previous in-flight
// request when the next 3-second tick fires before the previous one resolved.
const pollControllers = new Map<string, AbortController>();

/** Reset active session tracking. Aborts any in-flight requests and clears all state.
 * Call when the chat widget closes, or in test beforeEach to prevent state leaking between tests. */
export function resetActiveSession(): void {
  pollControllers.forEach(c => c.abort());
  activeSessionId = null;
  pollControllers.clear();
}

// Mirrors the axios interceptor's auth resolution (localStorage session, then `ht`
// cookie). Duplicated here because the Cerebra endpoints use raw fetch/SSE, which the
// axios request interceptor does not touch — so the token must be attached manually.
function getAuthToken(): string | null {
  const sessionId = localStorage.getItem(SESSION_KEY);
  if (sessionId) return sessionId;

  const match = /(?:^|;\s*)ht=([^;]*)/.exec(document.cookie);
  return match ? decodeURIComponent(match[1]) : null;
}

function isAbortError(err: unknown): boolean {
  return err instanceof Error && err.name === 'AbortError';
}

/** Report a failed backend request. */
function trackApiError(
  track: ChatbotEventTracker | undefined,
  api: string,
  detail: Record<string, unknown>
): void {
  track?.(ChatbotTracking.TRACKER_CHATBOT_API_ERROR, { api, ...detail });
}

// ─── Backend error messages ────────────────────────────────────────────────
// Every Cerebra error response is `{ code, detail, trace_id }`; `detail` is a
// single customer-safe sentence (already ending with a support ref), so we surface
// it as-is. SSE stream errors use the same shape with `value` in place of `detail`.
// The toolkit only needs a message string — it reads `err.message` (or takes the
// `onError` string) — so that is all we extract.
const GENERIC_ERROR_MESSAGE =
  'Something went wrong. Please try again, or reach us at support@hevodata.com.';

/** Read a non-ok response's `detail` message, falling back to a generic sentence. */
async function errorMessageFromResponse(res: Response): Promise<string> {
  try {
    const body = await res.json();
    if (body && typeof body.detail === 'string') return body.detail;
  } catch {
    // Non-JSON error body (proxy/gateway) — fall through to the generic message.
  }
  return GENERIC_ERROR_MESSAGE;
}

/**
 * Runs a fetch while reporting reliability analytics for it:
 *  - `SLOW_RESPONSE` if the request has not responded within the threshold
 *  - `API_ERROR` (reason: 'network') if it rejects for a non-abort reason
 *
 * AbortErrors are intentional cancellations (session switch, overlapping poll, etc.)
 * and are deliberately NOT reported here — the call sites where an abort means the
 * user navigated away report `REQUEST_ABORTED` themselves. Non-ok HTTP responses are
 * likewise left to callers, which know whether a given status is actually an error
 * (e.g. `pollMessages` treats 429 as a no-op).
 */
async function instrumentedFetch(
  track: ChatbotEventTracker | undefined,
  api: string,
  run: () => Promise<Response>
): Promise<Response> {
  let slowTimer: ReturnType<typeof setTimeout> | undefined;
  if (track) {
    slowTimer = setTimeout(
      () => track(ChatbotTracking.TRACKER_CHATBOT_SLOW_RESPONSE, {
        api,
        thresholdMs: SLOW_RESPONSE_THRESHOLD_MS
      }),
      SLOW_RESPONSE_THRESHOLD_MS
    );
  }
  try {
    return await run();
  } catch (err) {
    if (!isAbortError(err)) {
      trackApiError(track, api, {
        reason: 'network',
        message: err instanceof Error ? err.message : String(err)
      });
    }
    throw err;
  } finally {
    if (slowTimer !== undefined) clearTimeout(slowTimer);
  }
}

async function authFetch(
  path: string,
  init: RequestInit = {},
  track?: ChatbotEventTracker,
  api?: string
): Promise<Response> {
  const token = getAuthToken();
  return instrumentedFetch(track, api ?? path, () =>
    fetch(`${CEREBRA_BASE}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init.headers
      }
    })
  );
}

async function authFetchSSE(
  path: string,
  body: object,
  signal?: AbortSignal,
  track?: ChatbotEventTracker,
  api?: string
): Promise<Response> {
  const token = getAuthToken();
  return instrumentedFetch(track, api ?? path, () =>
    fetch(`${CEREBRA_BASE}${path}`, {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify(body)
    })
  );
}

async function consumeSSE(
  response: Response,
  callbacks: StreamCallbacks,
  track?: ChatbotEventTracker,
  api = 'aiStream',
  conversationId: string | null = null,
  startedAt?: number
): Promise<void> {
  if (!response.ok) {
    // Show the backend's customer-safe `detail` message.
    trackApiError(track, api, { status: response.status });
    callbacks.onError?.(await errorMessageFromResponse(response));
    return;
  }

  if (!response.body) return;
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // Per-turn analytics state for the AI-response lifecycle.
  let resolvedConversationId = conversationId;
  let sourceCount = 0;
  let firstTokenAt: number | undefined;

  while (true) {
    let done: boolean;
    let value: Uint8Array | undefined;
    try {
      ({ done, value } = await reader.read());
    } catch (err) {
      // Session switched — fetch was aborted. Exit silently without calling onError.
      if (isAbortError(err)) {
        track?.(ChatbotTracking.TRACKER_CHATBOT_REQUEST_ABORTED, { api });
        return;
      }
      throw err;
    }
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const data = line.slice(6).trim();
      if (!data) continue;

      try {
        const parsed = JSON.parse(data);
        switch (parsed.type) {
          case 'conversation':
            resolvedConversationId = parsed.conversationId ?? resolvedConversationId;
            callbacks.onConversationId?.(parsed.conversationId);
            break;
          case 'text':
            if (firstTokenAt === undefined) firstTokenAt = Date.now();
            callbacks.onChunk(parsed.value);
            break;
          case 'sources':
            sourceCount = Array.isArray(parsed.sources) ? parsed.sources.length : 0;
            callbacks.onSources?.(parsed.sources);
            break;
          case 'clarification': {
            const questions = parsed.questions ?? [];
            track?.(ChatbotTracking.TRACKER_CHATBOT_CLARIFICATION_REQUESTED, {
              conversationId: resolvedConversationId,
              questionCount: questions.length
            });
            callbacks.onClarification?.({ questions });
            break;
          }
          case 'done':
            track?.(ChatbotTracking.TRACKER_CHATBOT_AI_RESPONSE, {
              conversationId: resolvedConversationId,
              turnIndex: parsed.turnIndex ?? 0,
              hasSources: sourceCount > 0,
              sourceCount,
              ...(startedAt !== undefined ? { responseTimeMs: Date.now() - startedAt } : {}),
              ...(startedAt !== undefined && firstTokenAt !== undefined
                ? { timeToFirstTokenMs: firstTokenAt - startedAt }
                : {})
            });
            callbacks.onDone(parsed.turnIndex ?? 0);
            // Reset per-turn state in case the stream carries another turn.
            sourceCount = 0;
            firstTokenAt = undefined;
            break;
          case 'handoff':
            track?.(ChatbotTracking.TRACKER_CHATBOT_AI_HANDOFF, {
              conversationId: resolvedConversationId
            });
            callbacks.onHandoff?.();
            break;
          case 'error':
            // SSE error frame: `value` plays the role of `detail`.
            trackApiError(track, api, { reason: 'stream' });
            callbacks.onError?.(parsed.value ?? GENERIC_ERROR_MESSAGE);
            break;
        }
      } catch {
        // non-JSON line — ignore
      }
    }
  }
}

export function makeCerebraAIConfig(track?: ChatbotEventTracker): ChatWidgetAIConfig {
  return {
    async sendMessage(
      message: string,
      conversationId: string | null,
      callbacks: StreamCallbacks,
      files?: File[],
      signal?: AbortSignal
    ) {
      // Track the send action only — never the message text (only its length).
      track?.(ChatbotTracking.TRACKER_CHATBOT_MESSAGE_SENT, {
        conversationId,
        isNewConversation: !conversationId,
        messageLength: message.length,
        hasAttachments: !!files?.length,
        attachmentCount: files?.length ?? 0
      });

      // The widget owns cancellation — it aborts `signal` on new chat, session
      // switch, or unmount, which tears down this stream at the network layer.
      const startedAt = Date.now();
      let res: Response;

      if (files?.length) {
        const formData = new FormData();
        formData.append('type', 'message');
        formData.append('message', message);
        if (conversationId) formData.append('conversationId', conversationId);
        files.forEach(f => formData.append('files', f));

        const token = getAuthToken();
        res = await instrumentedFetch(track, 'sendMessage', () =>
          fetch(`${CEREBRA_BASE}/api/ai/stream`, {
            method: 'POST',
            signal,
            headers: {
              Accept: 'text/event-stream',
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: formData
          })
        );
      } else {
        const body: Record<string, unknown> = { type: 'message', message };
        if (conversationId) body.conversationId = conversationId;
        res = await authFetchSSE('/api/ai/stream', body, signal, track, 'sendMessage');
      }

      return consumeSSE(res, callbacks, track, 'sendMessage', conversationId, startedAt);
    },

    async resumeClarification(
      answers: Record<string, string | string[]>,
      conversationId: string | null,
      callbacks: StreamCallbacks,
      signal?: AbortSignal
    ) {
      // Track the action and the number of answers only — never the answer values.
      track?.(ChatbotTracking.TRACKER_CHATBOT_CLARIFICATION_ANSWERED, {
        conversationId,
        answerCount: Object.keys(answers).length
      });

      const answersArray = Object.entries(answers).map(([id, answer]) => ({
        id,
        answer: Array.isArray(answer) ? answer.join(', ') : answer
      }));
      const body: Record<string, unknown> = { type: 'resume', answers: answersArray };
      if (conversationId) body.conversationId = conversationId;
      const startedAt = Date.now();
      const res = await authFetchSSE('/api/ai/stream', body, signal, track, 'resumeClarification');
      return consumeSSE(res, callbacks, track, 'resumeClarification', conversationId, startedAt);
    },

    async fetchHistory(): Promise<ChatSession[]> {
      const res = await authFetch('/api/conversations', {}, track, 'fetchHistory');
      if (!res.ok) {
        trackApiError(track, 'fetchHistory', { status: res.status });
        throw new Error(await errorMessageFromResponse(res));
      }
      const data = await res.json();
      return (data.conversations ?? []).map((c: any): ChatSession => ({
        conversationId: c.conversationId as string,
        title: (c.title as string) ?? 'Untitled',
        status: c.status as ConversationStatus,
        alreadyHandedOff: (c.alreadyHandedOff as boolean) ?? false,
        alreadyRaisedToSupport: (c.alreadyRaisedToSupport as boolean) ?? false,
        conversationClosed: (c.conversationClosed as boolean) ?? false,
        ticketStatus: (c.ticketStatus as string) ?? 'open',
        ticketId: (c.ticketId as number | null) ?? null,
        specialistTicketId: (c.specialistTicketId as number | null) ?? null,
        csatSubmitted: (c.csatSubmitted as boolean) ?? false,
        csatEligible: (c.csatEligible as boolean) ?? false,
        aiAnswerStatus: (c.aiAnswerStatus as AiAnswerStatus) ?? null,
        createdAt: c.createdAt as string,
        updatedAt: c.updatedAt as string
      }));
    },

    async fetchSession(sessionId: string): Promise<ChatSession> {
      const res = await authFetch(`/api/conversations/${sessionId}`, {}, track, 'fetchSession');
      if (!res.ok) {
        trackApiError(track, 'fetchSession', { status: res.status });
        throw new Error(await errorMessageFromResponse(res));
      }
      const data = await res.json();
      return {
        conversationId: data.conversationId as string,
        title: (data.title as string) ?? 'Untitled',
        status: data.status as ConversationStatus,
        alreadyHandedOff: (data.alreadyHandedOff as boolean) ?? false,
        alreadyRaisedToSupport: (data.alreadyRaisedToSupport as boolean) ?? false,
        conversationClosed: (data.conversationClosed as boolean) ?? false,
        ticketStatus: (data.ticketStatus as string) ?? 'open',
        ticketId: (data.ticketId as number | null) ?? null,
        specialistTicketId: (data.specialistTicketId as number | null) ?? null,
        csatSubmitted: (data.csatSubmitted as boolean) ?? false,
        csatEligible: (data.csatEligible as boolean) ?? false,
        // Drives the reopened-conversation shimmer + AI-answer poll: 'processing' while the
        // answer is still being generated server-side, 'completed'/'failed' otherwise.
        aiAnswerStatus: (data.aiAnswerStatus as AiAnswerStatus) ?? null,
        createdAt: data.createdAt as string,
        updatedAt: data.updatedAt as string
      };
    },

    async fetchSessionMessages(sessionId: string): Promise<{ messages: ChatMessage[]; session: ChatSession }> {
      // User opened a conversation (from history or session restore).
      track?.(ChatbotTracking.TRACKER_CHATBOT_CONVERSATION_OPENED, { conversationId: sessionId });

      // Mark this session as active. pollMessages suppresses delivery for all
      // other sessions so background polling never fills the pending buffer with
      // messages that fetchSessionMessages will also return — preventing duplicates.
      // (In-flight AI streams are torn down by the widget via the abort signal it
      // passes to sendMessage/resumeClarification, so no stream-abort is needed here.)
      activeSessionId = sessionId;
      // Abort any in-flight pollMessages request for this session. The toolkit's
      // stopPolling() cancels the interval timer but leaves the current async
      // tick running — if that tick completes it delivers the same messages that
      // fetchSessionMessages is about to return, causing duplicate delivery.
      pollControllers.get(sessionId)?.abort();
      pollControllers.delete(sessionId);

      const res = await authFetch(
        `/api/conversations/${sessionId}?messages=true`,
        {},
        track,
        'fetchSessionMessages'
      );
      if (!res.ok) {
        trackApiError(track, 'fetchSessionMessages', { status: res.status });
        throw new Error(await errorMessageFromResponse(res));
      }
      const data = await res.json();

      const session: ChatSession = {
        conversationId: data.conversationId as string,
        title: (data.title as string) ?? 'Untitled',
        status: data.status as ConversationStatus,
        alreadyHandedOff: (data.alreadyHandedOff as boolean) ?? false,
        alreadyRaisedToSupport: (data.alreadyRaisedToSupport as boolean) ?? false,
        conversationClosed: (data.conversationClosed as boolean) ?? false,
        ticketStatus: (data.ticketStatus as string) ?? 'open',
        ticketId: (data.ticketId as number | null) ?? null,
        specialistTicketId: (data.specialistTicketId as number | null) ?? null,
        csatSubmitted: (data.csatSubmitted as boolean) ?? false,
        csatEligible: (data.csatEligible as boolean) ?? false,
        // Drives the reopened-conversation shimmer + AI-answer poll: 'processing' while the
        // answer is still being generated server-side, 'completed'/'failed' otherwise.
        aiAnswerStatus: (data.aiAnswerStatus as AiAnswerStatus) ?? null,
        createdAt: data.createdAt as string,
        updatedAt: data.updatedAt as string
      };

      const messages: ChatMessage[] = (data.messages ?? []).map((m: any): ChatMessage => {
        // Map new type field to internal MessageRole
        const type = m.type as string;
        let role: MessageRole;
        if (type === 'user') role = MessageRole.User;
        else if (type === 'ai') role = MessageRole.Assistant;
        else role = MessageRole.Engineer; // 'agent' and 'system' both map to Engineer

        const attach = m.attachment as
          | { kind?: string; url?: string; mediaType?: string; name?: string }
          | undefined;

        return {
          id: `msg_${Math.random().toString(36).slice(2, 10)}`,
          role,
          content: (m.content as string) ?? '',
          timestamp: new Date(m.createdAt as string).getTime(),
          ...(m.createdAt ? { createdAt: m.createdAt as string } : {}),
          ...(m.turnIndex !== undefined ? { turnIndex: m.turnIndex as number } : {}),
          ...(m.sources ? { sources: m.sources as Source[] } : {}),
          ...(m.feedback ? { feedback: m.feedback } : {}),
          ...(m.feedbackComment ? { feedbackComment: m.feedbackComment as string } : {}),
          // Confirmation/system replies (e.g. "ticket raised") opt out of the like/dislike
          // row — preserve that on reopen so the feedback row stays hidden.
          ...(m.hideFeedback ? { hideFeedback: true } : {}),
          ...(type === 'agent' && m.name ? { engineerName: m.name as string } : {}),
          ...(m.clarificationData ? { clarificationData: m.clarificationData } : {}),
          ...(m.clarificationAnswers
            ? {
                clarificationAnswers: Array.isArray(m.clarificationAnswers)
                  ? Object.fromEntries(
                      (m.clarificationAnswers as { id: string; answer: string }[]).map(a => [
                        a.id,
                        a.answer
                      ])
                    )
                  : (m.clarificationAnswers as Record<string, string | string[]>)
              }
            : {}),
          ...(attach
            ? {
                attachment: {
                  id: attach.url ?? `hist_${attach.name ?? ''}`,
                  name: attach.name ?? '',
                  size: 0,
                  mediaType: attach.mediaType ?? 'application/octet-stream',
                  ...(attach.url ? { url: attach.url } : {})
                }
              }
            : {})
        };
      });

      return { messages, session };
    },

    async submitFeedback(
      conversationId: string,
      turnIndex: number,
      rating: FeedbackRating,
      comment?: string,
      reason?: string
    ) {
      // Track the rating action — record the selected reason (a bounded, predefined value)
      // and whether a comment was left + its length, but never the comment text itself.
      // turnIndex === -1 flags support-experience feedback (not tied to a specific answer).
      const trimmedComment = comment?.trim() ?? '';
      track?.(ChatbotTracking.TRACKER_CHATBOT_FEEDBACK_SUBMITTED, {
        conversationId,
        turnIndex,
        rating,
        isSupportFeedback: turnIndex === -1,
        hasComment: trimmedComment.length > 0,
        commentLength: trimmedComment.length,
        ...(reason ? { reason } : {})
      });

      const res = await authFetch(
        '/api/feedback',
        {
          method: 'POST',
          body: JSON.stringify({
            conversationId,
            turnIndex,
            rating,
            comment: comment ?? null,
            reason: reason ?? null
          })
        },
        track,
        'submitFeedback'
      );
      if (!res.ok) {
        trackApiError(track, 'submitFeedback', { status: res.status });
        throw new Error(await errorMessageFromResponse(res));
      }
    },

    async raiseIssue(
      conversationId: string,
      customerPriority: CustomerPriority,
      reason?: string,
      comment?: string
    ) {
      const trimmedComment = comment?.trim() ?? '';
      track?.(ChatbotTracking.TRACKER_CHATBOT_ISSUE_RAISED, {
        conversationId,
        customerPriority,
        hasComment: trimmedComment.length > 0,
        commentLength: trimmedComment.length,
        ...(reason ? { reason } : {})
      });

      const res = await authFetch(
        '/api/raise-issue',
        {
          method: 'POST',
          body: JSON.stringify({
            conversationId,
            customerPriority,
            reason: reason ?? null,
            comment: comment ?? null
          })
        },
        track,
        'raiseIssue'
      );
      if (!res.ok) {
        // e.g. 409 conflict — "An issue has already been raised …".
        trackApiError(track, 'raiseIssue', { status: res.status });
        throw new Error(await errorMessageFromResponse(res));
      }
      return res.json();
    },

    async checkHealth() {
      let healthy = false;
      try {
        const res = useFallback ? await authFetch('/healthcheck', {}, track, 'checkHealth') : await instrumentedFetch(track, 'checkHealth', () =>
          fetch('/h20/healthcheck/cerebra-service'));
        healthy = res.ok && (await res.json()).status === 'ok';
      } catch {
        healthy = false; // network failure already reported as an API error by instrumentedFetch
      }
      // The widget shows an "unavailable" banner whenever this resolves false, so
      // record that user-facing outcome regardless of the underlying cause.
      if (!healthy) track?.(ChatbotTracking.TRACKER_CHATBOT_UNAVAILABLE, {});
      return healthy;
    }
  };
}

export function makeCerebraZendeskConfig(track?: ChatbotEventTracker): ChatWidgetZendeskConfig {
  return {
    allowedFileTypes: ['image/*', 'text/plain', 'text/csv', 'application/pdf', 'application/json'],

    async handoff(conversationId: string) {
      track?.(ChatbotTracking.TRACKER_CHATBOT_SUPPORT_HANDOFF, { conversationId });

      const res = await authFetch(
        '/api/handoff',
        { method: 'POST', body: JSON.stringify({ conversationId }) },
        track,
        'handoff'
      );
      if (!res.ok) {
        trackApiError(track, 'handoff', { status: res.status });
        throw new Error(await errorMessageFromResponse(res));
      }
      return res.json();
    },

    async sendMessage(conversationId: string, text: string, files?: File[]) {
      // Track the send action only — never the message text.
      track?.(ChatbotTracking.TRACKER_CHATBOT_SUPPORT_MESSAGE_SENT, {
        conversationId,
        hasAttachments: !!files?.length,
        attachmentCount: files?.length ?? 0
      });

      if (files?.length) {
        const token = getAuthToken();
        const authHeaders = {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        };
        await Promise.all(
          files.map(file => {
            const formData = new FormData();
            formData.append('file', file);
            return instrumentedFetch(track, 'zendeskAttachmentUpload', () =>
              fetch(`${CEREBRA_BASE}/api/conversations/${conversationId}/attachments`, {
                method: 'POST',
                headers: authHeaders,
                body: formData
              })
            );
          })
        );
      }

      if (!text.trim()) return;

      const res = await authFetch(
        `/api/conversations/${conversationId}/messages`,
        { method: 'POST', body: JSON.stringify({ text }) },
        track,
        'zendeskSendMessage'
      );
      if (!res.ok) {
        trackApiError(track, 'zendeskSendMessage', { status: res.status });
        throw new Error(await errorMessageFromResponse(res));
      }
    },

    async pollMessages(conversationId: string, after: number) {
      // Abort previous in-flight poll for this conversation so overlapping ticks
      // (when a response takes longer than the 3s interval) don't pile up.
      pollControllers.get(conversationId)?.abort();
      const controller = new AbortController();
      pollControllers.set(conversationId, controller);

      let res: Response;
      try {
        res = await authFetch(
          `/api/conversations/${conversationId}/messages?after=${after}`,
          { signal: controller.signal },
          track,
          'pollMessages'
        );
      } catch (err) {
        // Only clean up our own controller — a newer tick may have already replaced it.
        if (pollControllers.get(conversationId) === controller) {
          pollControllers.delete(conversationId);
        }
        throw err; // AbortError is swallowed by the toolkit's catch — no-op
      }
      if (pollControllers.get(conversationId) === controller) {
        pollControllers.delete(conversationId);
      }

      // On 429 (rate limited) treat as "no new messages" rather than throwing.
      // Throwing keeps initialized=false in the toolkit's interval closure, trapping
      // every subsequent tick in an after=0 retry loop. Returning an empty result
      // lets the cursor fast-forward on the first successful response instead.
      if (res.status === 429) {
        return { messages: [], count: after, conversationClosed: false };
      }
      if (!res.ok) {
        trackApiError(track, 'pollMessages', { status: res.status });
        throw new Error(await errorMessageFromResponse(res));
      }
      const data = await res.json();

      const conversationClosed =
        (data.conversationClosed ?? data.conversation_closed) as boolean | undefined;

      // Background polling runs for ALL human sessions in historyList, not just
      // the open one. When a non-active session gets a new message, background
      // polling would buffer it in pendingMsgsRef. When the user later opens
      // that session, fetchSessionMessages ALSO returns it from the DB — causing
      // a duplicate. Fix: only deliver messages for the active session. For
      // background sessions, return count (so the cursor fast-forwards) but no
      // messages (so nothing accumulates in the pending buffer).
      if (activeSessionId !== null && conversationId !== activeSessionId) {
        const rawCount = (data.messages ?? []).length;
        return { messages: [], count: after + rawCount, conversationClosed };
      }

      const messages = (data.messages ?? []).map((m: any) => ({
        author: m.author as string,
        name: m.name as string | undefined,
        text: (m.text ?? '') as string,
        receivedAt: (m.receivedAt ?? m.received_at ?? new Date().toISOString()) as string,
        attachment: m.attachment
      }));

      // count must be absolute (cumulative cursor), not a relative delta.
      // If the backend returns a relative count (e.g. 0 when no new messages),
      // the cursor resets to 0 on the next tick and re-fetches everything.
      return {
        messages,
        count: after + messages.length,
        conversationClosed
      };
    },

    async fetchCsatLinks(conversationId: string, signal?: AbortSignal) {
      let res: Response;
      try {
        res = await authFetch(
          `/api/conversations/${conversationId}/csat-links`,
          { signal },
          track,
          'fetchCsatLinks'
        );
      } catch (err) {
        // The widget aborts this when the user navigates away from the closing
        // conversation before the CSAT survey opens.
        if (isAbortError(err)) {
          track?.(ChatbotTracking.TRACKER_CHATBOT_REQUEST_ABORTED, { api: 'fetchCsatLinks' });
        }
        throw err;
      }
      if (!res.ok) {
        trackApiError(track, 'fetchCsatLinks', { status: res.status });
        throw new Error(await errorMessageFromResponse(res));
      }
      const data = await res.json();
      return data.links;
    },

    async acknowledgeCsat(conversationId: string, rating?: number): Promise<void> {
      // `rating` is the 1–5 CSAT score the user clicked (passed by the widget) — the key
      // satisfaction metric for the dashboard.
      track?.(ChatbotTracking.TRACKER_CHATBOT_CSAT_ACKNOWLEDGED, {
        conversationId,
        ...(rating !== undefined ? { rating } : {})
      });

      const res = await authFetch(
        `/api/conversations/${conversationId}/csat-acknowledged`,
        { method: 'POST' },
        track,
        'acknowledgeCsat'
      );
      if (!res.ok) {
        trackApiError(track, 'acknowledgeCsat', { status: res.status });
        throw new Error(await errorMessageFromResponse(res));
      }
    }
  };
}
